import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Route, Switch, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AppLayout from "./components/AppLayout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Pautas from "@/pages/Pautas";
import Calendario from "@/pages/Calendario";
// Temas: página descontinuada (Agnes, jun/26 — fluxo migrado pro modal local
// dentro de /pautas, coluna "Sugestão de Tema"). Import e rota removidos.
// import Temas from "@/pages/Temas";
import Metricas from "@/pages/Metricas";
import UTMs from "@/pages/UTMs";
import Relatorios from "@/pages/Relatorios";
import Usuarios from "@/pages/Usuarios";
import LinhaEditorial from "@/pages/LinhaEditorial";
import BancoIdeias from "@/pages/BancoIdeias";
import MetasKPIs from "@/pages/MetasKPIs";
import Insights from "@/pages/Insights";
import AceitarConvite from "@/pages/AceitarConvite";
import AceitarConviteNovo from "@/pages/AceitarConviteNovo";
import AceitarConviteCliente from "@/pages/AceitarConviteCliente";
import ResetPassword from "@/pages/ResetPassword";
import RedefinirSenha from "@/pages/RedefinirSenha";
import MagicLink from "@/pages/MagicLink";
import ShareRedirect from "@/pages/ShareRedirect";
import AuthMagic from "@/pages/AuthMagic";
import Admin from "@/pages/Admin";
import RazMaster from "@/pages/RazMaster";
import Convites from "@/pages/Convites";
import ClickUpDashboard from "@/pages/ClickUpDashboard";
import PreviewFeed from "@/pages/PreviewFeed";
import Direcionador from "@/pages/Direcionador";
import Radar from "@/pages/Radar";
import Recusas from "@/pages/Recusas";
import AccessDenied from "@/pages/AccessDenied";
import NotFound from "@/pages/NotFound";

function RedirectTo({ to }: { to: string }) {
  const [, setLocation] = useLocation();
  useEffect(() => { setLocation(to, { replace: true }); }, [to, setLocation]);
  return null;
}

function ComingSoon({ title }: { title: string }) {
  return (
    <div className="h-full flex flex-col items-center justify-center gap-4 bg-[#F5F4FF] p-8">
      <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: "#EDE9FF" }}>
        <svg className="w-8 h-8 text-[#422BFF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <div className="text-center">
        <h1 className="text-[#1E1B4B] font-bold text-lg" style={{ fontFamily: "Poppins, sans-serif" }}>
          {title}
        </h1>
        <p className="text-zinc-400 text-sm mt-1">Módulo em desenvolvimento</p>
      </div>
    </div>
  );
}

function Protected({
  children,
  requiredRole,
  razEmailOnly,
}: {
  children: React.ReactNode;
  requiredRole?: ("admin" | "editor" | "viewer")[];
  razEmailOnly?: boolean;
}) {
  return (
    <ProtectedRoute requiredRole={requiredRole} razEmailOnly={razEmailOnly}>
      <AppLayout>
        {children}
      </AppLayout>
    </ProtectedRoute>
  );
}

function AppRoutes() {
  return (
    <Switch>
      <Route path="/login" component={Login} />
      <Route path="/convite/:token" component={AceitarConvite} />
      <Route path="/esqueci-senha" component={ResetPassword} />
      <Route path="/redefinir-senha/:token" component={RedefinirSenha} />
      <Route path="/redefinir-senha" component={RedefinirSenha} />
      <Route path="/m/:token" component={MagicLink} />
      <Route path="/auth/magic" component={AuthMagic} />

      <Route path="/c/:key">
        <Protected><ShareRedirect /></Protected>
      </Route>
      <Route path="/pautas">
        <Protected><Pautas /></Protected>
      </Route>

      <Route path="/calendario">
        <Protected><Calendario /></Protected>
      </Route>

      <Route path="/preview-feed">
        <Protected><PreviewFeed /></Protected>
      </Route>

      {/* Aba interna Raz — só e-mails @razconsulting.com.br (gate no server via razProcedure) */}
      <Route path="/direcionador">
        <Protected razEmailOnly><Direcionador /></Protected>
      </Route>

      {/* Radar de card parado (SLA) — Raz-only (gate no server via razProcedure) */}
      <Route path="/radar">
        <Protected razEmailOnly><Radar /></Protected>
      </Route>

      {/* Recusas (peças + temas + pautas) — Raz-only */}
      <Route path="/recusas">
        <Protected razEmailOnly><Recusas /></Protected>
      </Route>

      {/* /temas matada (Agnes, jun/26) — quem cair aqui é redirecionado pra /pautas.
          ATENÇÃO: o redirect FICA dentro de useEffect, não num IIFE inline.
          IIFE seria executado A CADA render de AppRoutes (bug 16-jun: loop infinito). */}
      <Route path="/temas">
        <RedirectTo to="/pautas" />
      </Route>

      <Route path="/metricas">
        <Protected><Metricas /></Protected>
      </Route>

      <Route path="/utms">
        <Protected><UTMs /></Protected>
      </Route>

      <Route path="/relatorios">
        <Protected><Relatorios /></Protected>
      </Route>

      <Route path="/usuarios">
        <Protected requiredRole={["admin"]}><Usuarios /></Protected>
      </Route>

      <Route path="/convites">
        <Protected requiredRole={["admin"]}><Convites /></Protected>
      </Route>

      <Route path="/admin">
        <Protected requiredRole={["admin"]}><Admin /></Protected>
      </Route>

      <Route path="/raz-master">
        <Protected requiredRole={["admin"]}><RazMaster /></Protected>
      </Route>

      {/* ClickUp Dashboard — só raz admin (verificação no server via razAdminProcedure) */}
      <Route path="/clickup">
        <Protected requiredRole={["admin"]}><ClickUpDashboard /></Protected>
      </Route>

      <Route path="/access-denied" component={AccessDenied} />
      <Route path="/aceitar-convite" component={AceitarConviteNovo} />
      <Route path="/aceitar-convite-cliente" component={AceitarConviteCliente} />

      <Route path="/linha-editorial">
        <Protected><LinhaEditorial /></Protected>
      </Route>

      <Route path="/banco-ideias">
        <Protected><BancoIdeias /></Protected>
      </Route>

      <Route path="/metas">
        <Protected><MetasKPIs /></Protected>
      </Route>

      <Route path="/insights">
        <Protected><Insights /></Protected>
      </Route>

      <Route path="/">
        <Protected><Dashboard /></Protected>
      </Route>
      <Route component={NotFound} />
    </Switch>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <AppRoutes />
          </TooltipProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
